import {Injectable} from '@angular/core';
import {HttpClient, HttpEventType, HttpRequest} from '@angular/common/http';
import {Observable, Subscriber, forkJoin} from 'rxjs';
import {HttpUtilsService} from '../_base/crud';
import {MessageModel} from '../models/message.model';
import {map, mergeMap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {PushNotificationService} from './push-notification.service';
import {UserModel} from '../models/user.model';
import { OKResponse } from '../_base/crud/models/query-models/query-results.model';
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop';

const API_MESSAGES_URL = `${environment.pathToAPI}/api/v1/messages`;

export type RemoteFileDatum = {
    url: string;
    filename: string;
    tags?: string;
}

@Injectable()
export class MessagesService {

    private cancelSubscriber: Subscriber<boolean>
    public $cancel: Observable<boolean> = new Observable((sub) => {
        this.cancelSubscriber = sub;
    });

    constructor(
        private http: HttpClient,
        private pushNotificationService: PushNotificationService,
        private httpUtils: HttpUtilsService) {
    }

    cancelMessage() {
        this.cancelSubscriber.next(true);
    }

    createMessage(message: MessageModel, mobileNotificationData: any, token: string | null = null): Observable<MessageModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders(token);
        return this.http.post<MessageModel>(API_MESSAGES_URL, message, {headers: httpHeaders}).pipe(map((value) => {
            if (mobileNotificationData) {
                this.pushNotificationService.sendPushNotificationToBackEnd(mobileNotificationData).subscribe((res) => {
                    console.log("pushNotificationService.sentPushNotificationToBackEnd", res);
                });
            }
            return value;
        }));
    }

    getFile(file: NgxFileDropEntry): Observable<File | undefined> {
        return new Observable((subscriber) => {
            if (file.fileEntry.isFile) {
                const fileEntry = file.fileEntry as FileSystemFileEntry;
                fileEntry.file((file) => {
                    subscriber.next(file);
                    subscriber.complete();
                })
            } else if (!file.fileEntry.isDirectory) {
                // If it's not a directory then it's a cropped base64 image
                subscriber.next(this.dataURItoBlob(file.fileEntry.name, file.relativePath));
                subscriber.complete();
            } else {
                subscriber.next(undefined);
                subscriber.complete();
            }
        })
    }

    createMessageWithFiles(message: MessageModel, mobileNotificationData: any, files: NgxFileDropEntry[]) {
        const observables = files.map((file) => this.getFile(file));
        return forkJoin(observables).pipe(mergeMap((result) => {
            const formData = new FormData();
            let i = 0;
            for (const file of result) {
                if (file !== undefined) {
                    formData.append(`file-${i}`, file, file.name);
                    i++;
                }
            }
            const message_json = JSON.stringify(message);
            const json_blob = new Blob([message_json], {type: "application/json"});
            formData.append("message.json", json_blob, "message.json");
            const headers = this.httpUtils.getHTTPHeadresWithoutContentTypeWithByPassNgsw();
            const req = new HttpRequest('POST', `${API_MESSAGES_URL}/with_files`, formData, {headers, reportProgress: true});
            return this.http.request<MessageModel>(req).pipe(
                    map((event) => {
                        if (event.type === HttpEventType.Response && mobileNotificationData) {
                            this.pushNotificationService.sendPushNotificationToBackEnd(mobileNotificationData).subscribe((res) => {
                                console.log(res);
                            });
                        }
                        return event;
                }))
        }));
    }

    createMessageWithFileUrls(messageData: MessageModel, remoteFileData: RemoteFileDatum[], mobileNotificationData: any) {
        const headers = this.httpUtils.getHTTPHeaders();
        return this.http.post<MessageModel>(
            `${API_MESSAGES_URL}/with_file_urls`,
            {
                messageData,
                remoteFileData,
            }, {headers}
        ).pipe(map((value) => {
            if (mobileNotificationData) {
                this.pushNotificationService.sendPushNotificationToBackEnd(mobileNotificationData).subscribe((res) => {
                    console.log("pushNotificationService.sentPushNotificationToBackEnd", res);
                });
            }
            return value;
        }));
    }

    updateMessage(message: MessageModel): Observable<MessageModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.put<MessageModel>(API_MESSAGES_URL + `/${message.id}`, message, {headers: httpHeaders});
    }

    updateMessageTranslations(message: MessageModel): Observable<MessageModel> {
        const httpHeader = this.httpUtils.getHTTPHeaders();
        return this.http.put<MessageModel>(API_MESSAGES_URL + `/${message.id}/translations`, message, {headers: httpHeader});
    }

    getMessage(messageId: number): Observable<MessageModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.get<MessageModel>(API_MESSAGES_URL + `/${messageId}`, {headers: httpHeaders});
    }

    deleteMessage(messageId: number): Observable<OKResponse> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const url = `${API_MESSAGES_URL}/${messageId}`;
        return this.http.delete<OKResponse>(url, {headers: httpHeaders});
    }

    createSystemMessage(conversationId: number,
                        currentUser: UserModel,
                        systemMessageBody: string,
                        bodyGeneratedFrom: string = '',
                        token: string | null = null): Observable<MessageModel> {
        const systemMessage = new MessageModel();
        systemMessage.conversationId = conversationId;
        systemMessage.createdBy = currentUser.id;
        systemMessage.language = currentUser.language;
        systemMessage.body = systemMessageBody;
        systemMessage.bodyGeneratedFrom = bodyGeneratedFrom;
        systemMessage.fromSystem = 1;
        return this.createMessage(systemMessage, null, token);
    }

    private dataURItoBlob(dataURI: string, filename: string) {
        let byteString;
        if (dataURI.split(',')[0].indexOf('base64') >= 0) {
            byteString = atob(dataURI.split(',')[1]);
        } else {
            byteString = unescape(dataURI.split(',')[1]);
        }
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new File([ia], filename, {type: mimeString});
    }
}
